import { useState, useCallback } from 'react';
import { playbookApi } from '../api/playbookApi';
import type { PlaybookEntity, PlaybookListFilters, UpstreamChanges } from '../types/playbook.types';

export function usePlaybookLibrary() {
  const [playbooks, setPlaybooks] = useState<PlaybookEntity[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [upstreamMap, setUpstreamMap] = useState<Record<string, UpstreamChanges>>({});

  const load = useCallback(async (filters?: PlaybookListFilters) => {
    setLoading(true);
    setError(null);
    try {
      const res = await playbookApi.listPlaybooks(filters);
      const data = res as any;
      const items: PlaybookEntity[] = data.playbooks || data || [];
      setPlaybooks(items);
      setTotal(data.total || items.length);

      // Load upstream changes for firm variants
      const firmVariants = items.filter((p: PlaybookEntity) => p.source_playbook_id && !p.is_system);
      if (firmVariants.length > 0) {
        const upMap: Record<string, UpstreamChanges> = {};
        await Promise.allSettled(
          firmVariants.map(async (p: PlaybookEntity) => {
            try {
              const upRes = await playbookApi.getUpstreamChanges(p.id);
              upMap[p.id] = upRes as any;
            } catch { /* silent */ }
          })
        );
        setUpstreamMap(upMap);
      }
    } catch (err) {
      console.error('[usePlaybookLibrary] Failed to load:', err);
      setError(err instanceof Error ? err.message : 'Failed to load playbooks');
    } finally {
      setLoading(false);
    }
  }, []);

  const clonePlaybook = useCallback(async (playbookId: string) => {
    const res = await playbookApi.clonePlaybook(playbookId);
    return res as any as PlaybookEntity;
  }, []);

  const cloneSystemPlaybook = useCallback(async (playbookId: string) => {
    const res = await playbookApi.cloneSystemPlaybook(playbookId);
    return res as any as PlaybookEntity;
  }, []);

  const publishPlaybook = useCallback(async (playbookId: string) => {
    const res = await playbookApi.publishPlaybook(playbookId);
    return res as any as PlaybookEntity;
  }, []);

  const archivePlaybook = useCallback(async (playbookId: string) => {
    await playbookApi.archivePlaybook(playbookId);
  }, []);

  return {
    playbooks, total, loading, error, upstreamMap,
    load, clonePlaybook, cloneSystemPlaybook, publishPlaybook, archivePlaybook,
  };
}
